/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import { CircleCheck, CircleX, Server } from "lucide-react";

import { MetalHubServer } from "@src/@types/MetalHub";
import IconTile from "@src/components/ui/IconTile";
import {
  Cell,
  FlexCell,
  ListRow,
  Mono,
  Muted,
  Num,
  Pill,
  Primary,
  Secondary,
  Stack,
  formatDateTime,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

/** The On-prem servers table. */
export const METAL_HUB_COLUMNS: ListColumn[] = [
  { key: "hostname", label: "Hostname", width: "minmax(220px, 2fr)" },
  { key: "ip", label: "IP address", width: "minmax(130px, 1fr)" },
  { key: "status", label: "Agent status", width: "116px" },
  { key: "cpu", label: "CPU / Memory", width: "130px" },
  { key: "disks", label: "Disks", width: "120px" },
  { key: "seen", label: "Last seen", width: "120px" },
];

export const formatBytes = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, "")} ${units[i]}`;
};

/** The server's first routable address, else the host of its agent's API endpoint. */
export const serverAddress = (server: MetalHubServer) => {
  const ips = (server.nics || [])
    .flatMap(n => n.ip_addresses || [])
    .filter(ip => ip && !ip.startsWith("127.") && ip !== "::1" && !ip.toLowerCase().startsWith("fe80"));
  const v4 = ips.find(ip => ip.includes("."));
  if (v4 || ips[0]) return v4 || ips[0];
  try {
    return new URL(server.api_endpoint).hostname;
  } catch (e) {
    return "";
  }
};

export const OnlinePill = ({ active }: { active: boolean }) =>
  active ? (
    <Pill tone="success" icon={CircleCheck}>
      Online
    </Pill>
  ) : (
    <Pill tone="danger" icon={CircleX}>
      Offline
    </Pill>
  );

type Props = {
  item: MetalHubServer;
  selected: boolean;
  selectable?: boolean;
  onSelectedChange: (value: boolean) => void;
  onClick: () => void;
  actions?: DropdownAction[];
};
@observer
class MetalHubServerListItem extends React.Component<Props> {
  render() {
    const { item } = this.props;
    const os = [item.os_info?.os_name, item.os_info?.os_version].filter(Boolean).join(" ");
    const address = serverAddress(item);
    const disks = item.disks || [];
    const diskSize = disks.reduce((sum, d) => sum + (d.size || 0), 0);
    const cpu = item.logical_cores ? `${item.logical_cores} vCPU` : "";
    const mem = formatBytes(item.memory);
    return (
      <ListRow
        columns={METAL_HUB_COLUMNS}
        selectable={this.props.selectable}
        selected={this.props.selected}
        onSelectedChange={this.props.onSelectedChange}
        onClick={this.props.onClick}
        actions={this.props.actions}
        testId="metalHubListItem"
      >
        <FlexCell gap={12}>
          <IconTile icon={Server} size={28} />
          <Stack>
            <Primary title={item.hostname || ""}>{item.hostname || "No hostname"}</Primary>
            <Secondary title={os}>{os || <Muted>OS not reported</Muted>}</Secondary>
          </Stack>
        </FlexCell>
        <Cell title={item.api_endpoint}>
          {address ? <Mono>{address}</Mono> : <Muted>—</Muted>}
        </Cell>
        <div>
          <OnlinePill active={item.active} />
        </div>
        <Cell title={[cpu, mem].filter(Boolean).join(" · ")}>
          {cpu || mem ? [cpu, mem].filter(Boolean).join(" · ") : <Muted>—</Muted>}
        </Cell>
        <Num align="left" title={disks.map(d => `${d.name || d.path} ${formatBytes(d.size)}`).join("\n")}>
          {disks.length ? `${disks.length} · ${formatBytes(diskSize)}` : <Muted>—</Muted>}
        </Num>
        <Cell title={formatDateTime(item.updated_at)}>{timeAgo(item.updated_at)}</Cell>
      </ListRow>
    );
  }
}

export default MetalHubServerListItem;
